export const EXPENSE_STATUSES = [
  { value: 'draft', label: 'Brouillon', className: 'bg-secondary' },
  { value: 'pending', label: 'À payer', className: 'bg-warning text-dark' },
  { value: 'partially_paid', label: 'Partiellement payée', className: 'bg-info text-dark' },
  { value: 'paid', label: 'Payée', className: 'bg-success' },
  { value: 'cancelled', label: 'Annulée', className: 'bg-danger' },
] as const;

export const EXPENSE_PAYMENT_METHODS = [
  { value: 'cash', label: 'Espèces' },
  { value: 'bank_transfer', label: 'Virement bancaire' },
  { value: 'mobile_money', label: 'Mobile money' },
  { value: 'card', label: 'Carte bancaire' },
] as const;

export const RECURRING_EXPENSE_FREQUENCIES = [
  { value: 'daily', label: 'Chaque jour' },
  { value: 'weekly', label: 'Chaque semaine' },
  { value: 'monthly', label: 'Chaque mois' },
  { value: 'quarterly', label: 'Chaque trimestre' },
  { value: 'semiannual', label: 'Chaque semestre' },
  { value: 'annual', label: 'Chaque année' },
  { value: 'custom', label: 'Intervalle personnalisé' },
] as const;

type UnknownRecord = Record<string, any>;

export const expenseStatusLabel = (status: unknown): string =>
  EXPENSE_STATUSES.find((item) => item.value === String(status))?.label || String(status || '-');

export const expenseStatusClass = (status: unknown): string =>
  EXPENSE_STATUSES.find((item) => item.value === String(status))?.className || 'bg-secondary';

export const expensePaymentMethodLabel = (method: unknown): string =>
  EXPENSE_PAYMENT_METHODS.find((item) => item.value === String(method))?.label || String(method || '-');

export const recurringExpenseFrequencyLabel = (frequency: unknown): string =>
  RECURRING_EXPENSE_FREQUENCIES.find((item) => item.value === String(frequency))?.label || String(frequency || '-');

export const unwrapExpenseData = <T = UnknownRecord>(payload: unknown): T => {
  const envelope = payload as UnknownRecord | null;
  if (!envelope || typeof envelope !== 'object') return payload as T;

  const first = envelope.data;
  if (first && typeof first === 'object' && !Array.isArray(first) && 'data' in first) {
    return first.data as T;
  }

  return (first ?? envelope) as T;
};

export const expenseRows = <T = UnknownRecord>(payload: unknown): T[] => {
  if (Array.isArray(payload)) return payload as T[];
  const envelope = payload as UnknownRecord | null;
  if (!envelope || typeof envelope !== 'object') return [];
  if (Array.isArray(envelope.data)) return envelope.data as T[];
  if (Array.isArray(envelope.data?.data)) return envelope.data.data as T[];
  return [];
};

export const expensePagination = (payload: unknown): UnknownRecord => {
  const envelope = payload as UnknownRecord | null;
  return envelope?.meta?.pagination
    || envelope?.data?.meta?.pagination
    || envelope?.data?.pagination
    || {};
};

export const expenseTypeName = (expense: UnknownRecord): string =>
  expense?.expense_type?.name || expense?.type?.name || expense?.expense_type_name || '-';

export const expenseSupplierName = (expense: UnknownRecord): string =>
  expense?.supplier?.name || expense?.supplier_name || expense?.beneficiary || '-';

export const expenseDueAmount = (expense: UnknownRecord): number => {
  const explicit = Number(expense?.due_amount);
  if (Number.isFinite(explicit)) return explicit;
  return Math.max(0, Number(expense?.amount || 0) - Number(expense?.paid_amount || 0));
};

export const createClientUuid = (): string => {
  const browserCrypto = typeof crypto !== 'undefined'
    ? crypto as Crypto & { randomUUID?: () => string }
    : undefined;

  if (typeof browserCrypto?.randomUUID === 'function') {
    return browserCrypto.randomUUID();
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
    const random = Math.floor(Math.random() * 16);
    const value = character === 'x' ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
};

export const expenseAllowedAction = (expense: UnknownRecord, action: string): boolean => {
  if (Array.isArray(expense?.allowed_actions)) return expense.allowed_actions.includes(action);
  if (expense?.capabilities && typeof expense.capabilities[action] === 'boolean') {
    return expense.capabilities[action];
  }

  const status = String(expense?.status || '');
  if (action === 'update') return status === 'draft';
  if (action === 'validate') return status === 'draft';
  if (action === 'pay') return ['pending', 'partially_paid'].includes(status);
  if (action === 'cancel') return status !== 'cancelled';
  return true;
};
